import { DateTime } from 'luxon';
import { Base64URLToHex } from '../hex';
import * as leadtracking from '../leadtracking';
import { TryReCaptchaToken } from '../recaptcha';
import { Slugify } from '../slugify';
import { GetFirstAndLast } from '../users';
import { CreateFormSubmission, PollForResult } from './formsubmissions';
import * as models from './models';
import { GetPageRequestID } from './pagerequests';

/**
 * NewQuoteRequest submits a brand quote form as a `quote-request` form
 * submission. Quote-request is the deliberate synchronous exception (#6718
 * descoped it from the routing automations): the server processes it
 * in-request, so the create response already carries the created quote
 * request whole — no polling in the normal path, and the resolved object is
 * the same QuoteRequest the old synchronous endpoint returned, so brand pages
 * keep their redirect/success logic unchanged. It also runs the shared
 * success behavior (lead tracking).
 *
 * If in-request processing failed non-fatally, the create response has no
 * quoteRequest and this falls back to polling — a retry/re-run can still
 * fill the result in. If that times out too, the submission is still safely
 * persisted server-side, so this resolves successfully anyway (with an empty
 * quote request id and total, and no lead-tracking events, since there is
 * nothing to dedup against) — the brand page's success behavior doubles as
 * the "we got your request" fallback. A persisted submission is never
 * surfaced as an error.
 */
export async function NewQuoteRequest(quoteRequestInit: models.IQuoteRequestCreateRequest): Promise<models.QuoteRequest> {
    const quoteRequest = new models.QuoteRequestCreateRequest(quoteRequestInit);

    const createRequest = new models.QuoteRequestCreateRequest(quoteRequestInit);
    createRequest.referrer = location.href;
    createRequest.pageRequestID = await GetPageRequestID();
    createRequest.tz = new Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (quoteRequestInit.children?.length) {
        createRequest.children = [];
        for (const e of quoteRequestInit.children) {
            if (!e.child) {
                continue;
            }
            createRequest.children.push(new models.QuoteRequestCreateChild(e));
        }
    }

    if (quoteRequestInit.extras?.length) {
        createRequest.extras = [];
        for (const e of quoteRequestInit.extras) {
            if (!e.name) {
                continue;
            }
            createRequest.extras.push(new models.QuoteRequestCreateExtra(e));
        }
    }

    if (quoteRequestInit.dateNeeded) {
        const d = DateTime.fromISO(quoteRequestInit.dateNeeded);
        if (d.isValid) {
            createRequest.dateNeeded = d.toISODate() ?? '';
        } else {
            createRequest.dateNeeded = '';
            createRequest.extras.push(new models.QuoteRequestCreateExtra({
                name: 'Date Needed',
                value: quoteRequestInit.dateNeeded,
            }));
        }
    }

    const submission = await CreateFormSubmission({
        form: 'quote-request',
        // Fall back to the page path so no submission ever lands with a blank
        // Variant (#7255).
        variant: createRequest.form || location.pathname,
        payload: createRequest,
        uploadIDs: createRequest.uploadIDs,
        pageRequestID: createRequest.pageRequestID,
        reCaptcha: await TryReCaptchaToken('quote_request'),
    });

    const { firstName, lastName } = GetFirstAndLast(quoteRequest.name);

    let resp: models.QuoteRequest;
    if (submission.quoteRequest?.id) {
        // The create response carries the created quote request whole (quotes
        // included) — the same object the old synchronous endpoint returned,
        // so brand pages consume it directly.
        resp = submission.quoteRequest;
    } else {
        const result = await PollForResult(submission.formSubmissionID, {
            isComplete: formSubmission => Boolean(formSubmission.quoteRequestID),
        });

        // Rebuild the response shape brand pages consume from what we
        // submitted plus the poll result — the poll endpoint deliberately
        // exposes only the created ids, quote key, and total, never the
        // payload.
        resp = new models.QuoteRequest({
            id: result?.quoteRequestID ?? '',
            userID: result?.userID ?? '',
            formSubmissionID: submission.formSubmissionID,
            email: quoteRequest.email,
            phone: quoteRequest.phone,
            company: quoteRequest.company,
            firstName: firstName,
            lastName: lastName,
            quantity: quoteRequest.quantity,
            dateNeeded: createRequest.dateNeeded || undefined,
            form: quoteRequest.form,
            quotes: [{
                key: result?.quoteKey ?? '',
                cachedTotal: result?.quoteTotal ?? 0,
            }],
        });
    }

    if (resp.id) {
        // Hex-encode the quote request id so it matches the server-side
        // Conversions API event id (hex of the same bytes) and Meta/Google
        // dedup the browser and server lead events.
        const eventId = Base64URLToHex(resp.id);
        const contentCategory = quoteRequest.type ? Slugify(quoteRequest.type) : undefined;
        const contentName = quoteRequest.lineItem ? Slugify(quoteRequest.lineItem) : undefined;

        // No instant quote total → omit the value entirely. An explicit $0
        // makes every pixel event read as a valueless lead in platform
        // dashboards (#7834); the browser lead is deliberately non-optimizing
        // (#6790), so no value at all is the intent.
        const quoteTotal = resp.quotes[0]?.cachedTotal?.toNumber();

        leadtracking.TrackLead({
            eventId: eventId,
            value: quoteTotal || undefined,
            quoteRequestID: resp.id,
            userID: resp.userID,
            email: quoteRequest.email,
            phone: quoteRequest.phone,
            company: quoteRequest.company,
            firstName: firstName,
            lastName: lastName,
            lineItem: quoteRequest.lineItem,
            type: quoteRequest.type,
            quantity: quoteRequest.quantity,
            contentCategory: contentCategory,
            contentName: contentName,
        });
    }

    return resp;
}
